import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  ApiError,
  getRefund,
  type RefundRecord,
  type RefundState,
  type TicketState,
} from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';

/** numeric string → 加 `$` 與千分位（refund_amount 不帶 currency）。 */
function formatAmount(raw: string): string {
  const n = Number(raw);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : raw;
}

/** refund_state badge 顏色（PRD §E-2 12 態）。 */
function refundBadgeStyle(state: RefundState): React.CSSProperties {
  const terminal: RefundState[] = ['completed', 'cancelled', 'rejected'];
  const failed: RefundState[] = ['refund_failed', 'allowance_failed', 'manual_review'];
  const bg = terminal.includes(state) ? '#d1ecf1' : failed.includes(state) ? '#f8d7da' : '#fff3cd';
  const fg = terminal.includes(state) ? '#0c5460' : failed.includes(state) ? '#721c24' : '#856404';
  return {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    background: bg,
    color: fg,
  };
}

function ticketBadgeStyle(state: TicketState): React.CSSProperties {
  const palette: Record<TicketState, { bg: string; fg: string }> = {
    valid: { bg: '#d4edda', fg: '#155724' },
    used: { bg: '#d1ecf1', fg: '#0c5460' },
    void_pending_refund: { bg: '#fff3cd', fg: '#856404' },
    voided: { bg: '#f8d7da', fg: '#721c24' },
    expired: { bg: '#e2e3e5', fg: '#383d41' },
  };
  const p = palette[state];
  return {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    background: p.bg,
    color: p.fg,
  };
}

const TERMINAL_REFUND_STATES: readonly RefundState[] = ['completed', 'cancelled', 'rejected'];

function isTerminal(r: RefundRecord): boolean {
  return TERMINAL_REFUND_STATES.includes(r.refund_state);
}

/**
 * 退票進度頁 — `/refunds/:id`（AC-E2-7 雙欄）。
 *
 * - 顯示 (refund_state, ticket_state) 雙欄；嚴禁單一 status（AC-DM-1）
 * - refund_settled_but_allowance_pending=true 時提示折讓尚在處理
 * - manual_review=true 時提示已進人工複核
 * - 非終態以 10s polling 更新；終態停 polling
 */
export function RefundDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['refund', id],
    queryFn: () => {
      if (id === undefined) throw new Error('missing refund id');
      return getRefund(id);
    },
    enabled: id !== undefined,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 10_000;
      return isTerminal(data) ? false : 10_000;
    },
  });

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('refundDetail.title')}</h1>
        <Link to="/" className="topbar-back">
          {t('common.backHome')}
        </Link>
      </header>

      {query.isPending && <p>{t('common.loading')}</p>}
      {query.isError && (
        <p className="error-text">
          {t('refundDetail.loadFailed')}
          {query.error instanceof ApiError
            ? (query.error.problem?.detail ?? query.error.message)
            : t('common.unknownError')}
        </p>
      )}

      {query.isSuccess && (
        <article className="card">
          <div className="kv">
            <span className="kv-label">{t('refundDetail.refundNo')}</span>
            <span className="kv-value" style={{ fontFamily: 'monospace' }}>
              {query.data.id.slice(0, 8)}
            </span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('refundDetail.refundStatus')}</span>
            <span style={refundBadgeStyle(query.data.refund_state)}>{query.data.refund_state}</span>
          </div>
          <div className="kv">
            <span className="kv-label">{t('refundDetail.ticketStatus')}</span>
            {query.data.ticket_state ? (
              <span style={ticketBadgeStyle(query.data.ticket_state)}>
                {query.data.ticket_state}
              </span>
            ) : (
              <span style={{ color: '#777', fontSize: 12 }}>—</span>
            )}
          </div>
          <div className="kv">
            <span className="kv-label">{t('refundDetail.refundAmount')}</span>
            <span className="kv-value" style={{ fontSize: 17 }}>
              {formatAmount(query.data.refund_amount)}
            </span>
          </div>
          <hr className="divider" />

          {query.data.refund_settled_but_allowance_pending && (
            <p style={{ fontSize: 13, color: '#856404' }}>{t('refundDetail.allowancePending')}</p>
          )}
          {query.data.manual_review && (
            <p style={{ fontSize: 13, color: '#721c24' }}>{t('refundDetail.manualReview')}</p>
          )}

          {!isTerminal(query.data) && (
            <p style={{ fontSize: 12, color: '#777', marginTop: 12 }}>
              {t('refundDetail.pollingNote')}
            </p>
          )}

          {query.data.ticket_id !== null && (
            <p style={{ marginTop: 12 }}>
              <Link to={`/tickets/${query.data.ticket_id}`}>{t('refundDetail.viewTicket')}</Link>
            </p>
          )}
        </article>
      )}
    </div>
  );
}
